'use client';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, SxProps, Theme, Typography } from '@mui/material';

import { Avatar } from '@/components';
import type { EventResponse } from '@/types';
import { SearchSectionHeader } from './SearchSectionHeader';

interface RecentItemProps {
  item: EventResponse;
  onClick: (item: EventResponse) => void;
  onRemove: (id: string, e: React.MouseEvent) => void;
}

const RecentItem = ({ item, onClick, onRemove }: RecentItemProps) => {
  const handleClick = () => onClick(item);
  const handleRemove = (e: React.MouseEvent) => onRemove(item.id, e);

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.25,
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
          '& .close-icon': { opacity: 1 },
        },
      }}
    >
      <Avatar src={item.image} alt={item.title} size={36} />
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          fontWeight: 600,
          fontSize: '0.95rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'text.primary',
        }}
      >
        {item.title}
      </Typography>
      <IconButton
        className="close-icon"
        size="small"
        onClick={handleRemove}
        sx={{
          opacity: 0,
          transition: 'opacity 0.2s',
          color: 'text.secondary',
          '&:hover': { color: 'error.main' },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

interface SearchRecentResultsProps {
  searches: EventResponse[];
  onResultClick: (item: EventResponse) => void;
  onRemove: (id: string, e: React.MouseEvent) => void;
  onClearAll: () => void;
  containerStyles?: SxProps<Theme>;
}

export const SearchRecentResults = ({
  searches,
  onResultClick,
  onRemove,
  onClearAll,
  containerStyles = { p: 2.5, pt: 1 },
}: SearchRecentResultsProps) => {
  const renderRecentItem = (item: EventResponse) => (
    <RecentItem key={item.id} item={item} onClick={onResultClick} onRemove={onRemove} />
  );

  const renderRecentItemList = searches?.map(renderRecentItem);
  return (
    <Box sx={containerStyles}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <SearchSectionHeader title="Recent" />
        <Button
          size="small"
          onClick={onClearAll}
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'text.secondary',
            minWidth: 'auto',
            px: 1,
            py: 0.5,
            '&:hover': {
              color: 'error.main',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
            },
          }}
        >
          Clear All
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>{renderRecentItemList}</Box>
    </Box>
  );
};
